<script>
import Draggable from 'vuedraggable';
import TagInput from '@app/components/TagInput.vue';
import { debounce, isEqual } from 'lodash';
import MembershipItem from '@app/components/MembershipItem.vue';
import MembershipForm from '@app/components/MembershipForm.vue';

const DEFAULT_SEARCH_FORM_DATA = {
  q: '',
  tags: [],
  sort_by: '',
  order_by: 'asc',
};

export default {
  components: {
    Draggable,
    TagInput,
    MembershipItem,
  },
  head: {
    title: 'Vos Abonnements',
  },
  created() {
    this.isFetching = true;
    this.$store.commit('memberships/setSearchParams', this.searchFormData);
    this.$store.dispatch('memberships/count')
      .then(() => this.$store.dispatch('memberships/fetch'))
      .finally(() => (this.isFetching = false));
  },
  data: () => ({
    isFetching: false,
    searchFormData: { ...DEFAULT_SEARCH_FORM_DATA },
  }),
  computed: {
    count() {
      return this.$store.getters['memberships/count'];
    },
    memberships: {
      set(value) {
        this.$store.dispatch('memberships/reorder', value)
          .then(() => this.$showMessage.success());
      },
      get() {
        return (
          this.$store.state.memberships.list
          && this.$store.state.memberships.list.data
        );
      },
    },
    hasPercentPlan() {
      return this.$store.getters['auth/hasPercentPlan'];
    },
    hasExpertPlan() {
      return this.$store.getters['auth/hasExpertPlan'];
    },
    hasProPlan() {
      return this.$store.getters['auth/hasProPlan'];
    },
    authStore() {
      return this.$store.getters['auth/store'];
    },
    debouncedSearch() {
      return debounce(this.search, 300);
    },
    canReorder() {
      return isEqual(DEFAULT_SEARCH_FORM_DATA, this.searchFormData);
    },
    searchParamsDirty() {
      return !isEqual(DEFAULT_SEARCH_FORM_DATA, this.searchFormData);
    },
    membershipLimit() {
      if (this.hasPercentPlan) {
        return 0;
      } if (this.hasProPlan) {
        return 1;
      } if (this.hasExpertPlan) {
        return 5;
      }
      return 0;
    },
  },
  methods: {
    async search() {
      this.isFetching = true;
      await this.$store.commit('memberships/setSearchParams', this.searchFormData);
      await this.$store.dispatch('memberships/fetch');
      this.isFetching = false;
    },
    toggleOrder() {
      this.searchFormData.order_by = this.searchFormData.order_by !== 'desc' ? 'desc' : 'asc';
      this.search(true);
    },
    resetSearchParams() {
      return this.searchFormData = { ...DEFAULT_SEARCH_FORM_DATA };
    },
    addMembership() {
      if (this.hasPercentPlan) {
        return this.$showMessage.goPro();
      }

      return this.$buefy.modal.open({
        parent: this,
        component: MembershipForm,
        hasModalCard: true,
        trapFocus: true,
      });
    },
    handleSortByChange(value) {
      this.searchFormData.sort_by = value;
      if (value !== '') {
        this.search();
      } else {
        return this.searchFormData.order_by = 'asc';
      }
      return this.searchFormData.order_by = 'asc';
    },
  },
  watch: {
    searchFormData: {
      handler() {
        this.debouncedSearch();
      },
      deep: true,
    },
  },
};
</script>

<template>
  <section>
    <header class="level mb-0">
      <div class="level-left is-block w-80p">
        <h1 class="title is-size-4-mobile mb-2 is-flex is-align-items-center">
          <span>Vos Abonnements</span>
          <span v-if="!hasPercentPlan" class="ml-2">
            ({{ $store.state.memberships.count }} / {{ membershipLimit }})
          </span>
          <span v-if="isFetching" class="loader is-primary is-size-5 ml-4" />
        </h1>
      </div>
      <div class="level-right is-hidden-mobile">
        <b-button type="is-primary" icon-left="plus" @click="addMembership">
          Ajouter un abonnement
        </b-button>
      </div>
    </header>
    <template v-if="count === null || memberships === null">
      <div>
        <b-skeleton height="200" />
        <b-skeleton height="200" />
        <b-skeleton height="200" />
      </div>
    </template>
    <GoProWrapper :hasSep="false">
      <template v-if="count <= 0">
        <EmptyState
          title="Vous n'avez pas encore d'abonnement"
          text="Cliquez sur le bouton ci-dessous pour en créer un dès maintenant."
          cta-text="Ajouter un abonnement"
          :cta-func="addMembership" />
      </template>
      <template v-else>
        <h3 class="mb-8 is-size-8">
          Créez vos abonnements en regroupant plusieurs formations pour offrir
          à vos apprenants une expérience complète<br>à un tarif récurrent avantageux.
          L'ordre d'affichage par défaut de vos abonnements sera le même dans votre
          <a :href="$store.getters['auth/url']" target="_blank">
            page d'accueil
          </a>.
        </h3>

        <form @submit.prevent="search" class="mb-5">
          <div class="columns">
            <div class="column">
              <b-field label="Filtrer par">
                <b-input
                  v-model="searchFormData.q"
                  placeholder="Filtrer par nom ou description"
                  :loading="isFetching" />
              </b-field>
            </div>

            <div class="column">
              <b-field label="Étiquette(s)">
                <TagInput
                  v-model="searchFormData.tags"
                  placeholder="Filtrer par étiquette" />
              </b-field>
            </div>

            <div class="column is-narrow">
              <b-field label="Trier par">
                <b-select :value="searchFormData.sort_by" @input="handleSortByChange">
                  <option value="" disabled>Ordre d'affichage public</option>
                  <option value="created_at">Date de création</option>
                  <option value="name">Nom</option>
                  <option value="active_subscribers_count">Nombre d'abonnés</option>
                </b-select>
                <p class="control">
                  <b-tooltip :label="searchFormData.order_by == 'asc' ? 'croissant' : 'décroissant'">
                    <b-button
                      :icon-left="searchFormData.order_by == 'asc' ? 'arrow-up' : 'arrow-down'"
                      @click="toggleOrder" />
                  </b-tooltip>
                </p>
              </b-field>
            </div>
          </div>
        </form>

        <a
          v-if="searchParamsDirty"
          href="#"
          class="is-size-8 level-right mb-6"
          style="cursor:pointer;"
          @click.prevent="resetSearchParams">
          <strong>Réinitialiser les paramètres de recherche</strong>
        </a>

        <draggable
          v-if="(memberships || []).length > 0"
          v-model="memberships"
          handle=".handle">
          <MembershipItem
            v-for="membership in memberships"
            :key="membership.uuid"
            v-bind="{ membership }"
            :reorderable="canReorder"
            @selected-tag="searchFormData.tags = [...searchFormData.tags, $event.tag]" />
        </draggable>

        <EmptyState
          v-else
          title="Aucun résultat trouvé"
          text="Aucun abonnement ne correspond à votre recherche."
          cta-text="Réinitialiser les paramètres de recherche"
          :cta-func="resetSearchParams" />
      </template>
    </GoProWrapper>
  </section>
</template>

<style lang="scss" scoped>
::v-deep .gpwcomp {
  padding-top: 1rem;
}
</style>
